/**
 * LES BILLETS DES ASSOCIATIONS, FORMULE PAR FORMULE — ce que les écrans en
 * disent, sans React (19/10/2026).
 *
 * Le serveur compte (`ticketing/formules.ts`) ; ce fichier écrit les nombres
 * et les petites phrases des tuiles « Venue des associations » et
 * « Formules pour le bar », de la ligne d'une association rattachée, et de
 * la carte de la soirée en cours.
 */

export type CleFormule = 'ESSENTIEL' | 'SOFT' | 'PRESTIGE' | 'AUTRE'

export interface CompteFormule {
  cle: CleFormule
  libelle: string
  contenu: string | null
  vendus: number
  entres: number
  pas_entres: number
  absents_pct: number | null
}

export interface VenueAssociation {
  id: string
  name: string
  vendus: number
  entres: number
  pas_entres: number
  absents_pct: number | null
  formules: CompteFormule[]
}

export interface VenueFormules {
  fini: boolean
  /** La nuit a commencé (ou un rattaché a déjà scanné). Absent (ancien serveur) : commencée. */
  commencee?: boolean
  /** Pretix relu en entier après la fin de la nuit : seule condition d'un taux d'absents. Absent : complète. */
  lecture_complete?: boolean
  /** La dernière lecture de Pretix (ISO) ; `null` = jamais lue. */
  lu_a?: string | null
  vendus: number
  entres: number
  pas_entres: number
  absents_pct: number | null
  formules: CompteFormule[]
  associations: VenueAssociation[]
  phrase_venue: string
  phrase_bar: string
}

const nombre = (n: number) => n.toLocaleString('fr-FR')

/** « 12,7 % » — une décimale, à la française. */
export function pctFr(x: number): string {
  return `${x.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
}

/**
 * La ligne d'une association, de l'Accueil à la fiche : « 249 billets · 224
 * entrés · 10 % absents » ; avant la fin de la nuit, « 25 pas encore entrés ».
 * La nuit finie mais Pretix pas relu en entier : « lecture Pretix
 * incomplète », jamais « tous venus » ni un taux (revue du 19/10/2026).
 * `sans_entres` : la ligne du bloc « Billetteries de la soirée », dont la
 * colonne dit déjà les entrées — un seul nombre, d'une seule source. PURE.
 */
export function ligneVenue(
  v: { vendus: number; entres: number; pas_entres: number; absents_pct: number | null }, fini: boolean,
  o: { lecture_complete?: boolean | undefined; sans_entres?: boolean } = {},
): string {
  const base = o.sans_entres
    ? `${nombre(v.vendus)} billet${v.vendus > 1 ? 's' : ''}`
    : `${nombre(v.vendus)} billet${v.vendus > 1 ? 's' : ''} · ${nombre(v.entres)} entré${v.entres > 1 ? 's' : ''}`
  if (fini && o.lecture_complete === false) return `${base} · lecture Pretix incomplète`
  if (fini) return v.absents_pct === null || v.pas_entres === 0 ? `${base} · tous venus` : `${base} · ${pctFr(v.absents_pct)} absents`
  return v.pas_entres === 0 ? base : `${base} · ${nombre(v.pas_entres)} pas encore entré${v.pas_entres > 1 ? 's' : ''}`
}

/** « il y a 4 min », « il y a 2 h » — l'âge d'une lecture. PURE. */
export function ilYA(iso: string, nowMs: number): string {
  const s = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000))
  if (s < 60) return 'il y a moins d’une minute'
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  if (s < 86400 * 2) return `il y a ${Math.round(s / 3600)} h`
  return `il y a ${Math.round(s / 86400)} jours`
}

/**
 * La note sous la tuile « Venue des associations », selon le moment — PURE.
 * Avant la nuit, elle ne parle pas d'une porte qui « scanne encore » ; la nuit
 * finie sans lecture complète, elle dit pourquoi il n'y a pas de taux.
 */
export function noteVenue(v: Pick<VenueFormules, 'fini' | 'commencee' | 'lecture_complete'>): string {
  const fin = ' Billets Pretix seulement — Shotgun a sa tuile Tarifs.'
  if (v.fini && v.lecture_complete === false) return `Pretix n’a pas encore été relu en entier après la fin de la nuit : un billet sans entrée peut n’être qu’un scan pas encore lu.${fin}`
  if (v.fini) return `Absents : billets payés jamais scannés à la porte.${fin}`
  if (v.commencee === false) return `La porte n’a pas encore ouvert : les entrées s’afficheront dès les premiers scans.${fin}`
  return `Pendant la nuit, « pas encore entrés » : la porte scanne encore.${fin}`
}

/**
 * La note sous « Formules pour le bar » — PURE. La page se relit toutes les
 * 30 s, mais Pretix lui-même n'est lu que toutes les 30 s pendant la nuit et
 * toutes les 15 min sinon : l'âge de la dernière lecture dit la vérité.
 */
export function noteBar(v: Pick<VenueFormules, 'fini' | 'lu_a'>, nowMs: number): string {
  const base = 'Chaque billet d’association comprend ses boissons, lues dans le nom de la formule sur Pretix.'
  if (v.fini) return base
  return v.lu_a ? `${base} Dernière lecture de Pretix : ${ilYA(v.lu_a, nowMs)}.` : `${base} Pretix n’a encore jamais été lu.`
}

/**
 * La ligne « Bar » de la carte de la soirée en cours sur l'Accueil : soumise au
 * réglage « tableau de bord » comme tous les chiffres Pretix de l'Accueil
 * (revue du 19/10/2026). Le serveur la retire déjà ; l'écran ne la montre pas
 * non plus face à un serveur plus ancien. PURE.
 */
export function barSurAccueil(formules: VenueFormules | null | undefined, a: { show_dashboard?: boolean } | null | undefined): formules is VenueFormules {
  return !!formules && formules.formules.length > 0 && a?.show_dashboard !== false
}

/** « Essentiel 96/110 · Soft 115/124 · Prestige 13/15 entrés » — la carte du direct. PURE. */
export function ligneBarCourte(formules: readonly CompteFormule[]): string {
  return `${formules.map((f) => `${f.libelle} ${nombre(f.entres)}/${nombre(f.vendus)}`).join(' · ')} entrés`
}

/** La part entrée d'une formule (0 → 1), pour la barre de la tuile. PURE. */
export function partEntree(f: Pick<CompteFormule, 'vendus' | 'entres'>): number {
  return f.vendus > 0 ? Math.min(1, f.entres / f.vendus) : 0
}

/**
 * Les tuiles de la fiche d'une soirée que les billets d'associations retirent
 * ou font apparaître — PURE. Sans billet d'association (ou réglage éteint),
 * les deux tuiles disparaissent : une tuile vide laisserait un trou.
 */
export function tuilesFormulesAbsentes(
  formules: VenueFormules | null | undefined,
  a: { show_no_show?: boolean; show_bar_formules?: boolean } | null | undefined,
): string[] {
  const out: string[] = []
  if (!formules || a?.show_no_show === false) out.push('association-venue')
  if (!formules || a?.show_bar_formules === false) out.push('bar-formules')
  return out
}
